import { FC, useEffect } from "react";
import classNames from "classnames";
import { Button, Checkbox, Form, Input, Modal, Select, Space } from "@payping/web-components";
import { unmaskStrToNum } from "@payping/utils/string";

import { useAppTranslation } from "@/utils/useAppTranslation";
import styles from "@/assets/scss/Shop/Invoice/scss/form.module.scss";
import { ConfigItemModalProps, Product } from "./type";
import { CuToman } from "@payping/icons";

const ConfigItemModal: FC<ConfigItemModalProps> = ({ item, maxQuantity, onCancel, onOk }) => {
  const visible = !!item;
  const [formRef] = Form.useForm();
  const { t } = useAppTranslation();

  useEffect(() => {
    formRef.setFieldsValue({
      ...item,
      haveTax: Boolean(item.taxtionAmount),
    });
  }, [formRef, item]);

  const footer = (
    <Space>
      <Button onClick={onCancel}>{t("cancel")}</Button>
      <Button type="primary" onClick={formRef.submit}>
        {t("ok")}
      </Button>
    </Space>
  );

  return (
    <Modal open={visible} title={t("product.edit.title")} onCancel={onCancel} footer={footer}>
      <Form
        className={classNames(styles.form, styles.visible)}
        layout={"vertical"}
        onFinish={(values) => {
          const aa = {
            ...item,
            ...values,
            amount: parseInt(values.amount, 10),
            defineAmountByUser: false,
            discountAmount: parseInt(values.discountAmount, 10),
            haveTax: values?.haveTax ?? false,
            tax: values?.haveTax ?? false,
            taxtionAmount: values?.haveTax === false ? 0 : item.taxtionAmount,
            unlimited: false,
          };
          onOk(aa as Product);
        }}
        form={formRef}
      >
        <Form.Item label={t("product.itemTitle")} rules={[{ required: true }]} name={"title"} className="tw-mt-[10px]">
          <Input />
        </Form.Item>
        <Form.Item
          label={t("product.count")}
          rules={[
            { required: true },
            maxQuantity
              ? {
                  max: maxQuantity,
                  message: t("error.itemExceeded"),
                  min: 1,
                  transform: (value): number => unmaskStrToNum(value),
                  type: "number",
                }
              : {
                  message: t("error.minItemCount"),
                  min: 1,
                  transform: (value): number => unmaskStrToNum(value),
                  type: "number",
                },
          ]}
          name={"quantity"}
          className="tw-mt-[10px]"
          initialValue={1}
        >
          <Input onlyNumbers />
        </Form.Item>
        <Form.Item
          label={t("product.price")}
          rules={[{ required: true }]}
          name={"amount"}
          className="tw-mt-[10px]"
          initialValue={0}
        >
          <Input.Number
            className="tw-w-full"
            suffix={<CuToman width={24} height={24} />}
            separateThousandth
            inputMode="numeric"
            changeOnWheel={false}
            controls={false}
          />
        </Form.Item>
        <Form.Item name={"haveTax"} className="tw-mt-[10px]" valuePropName="checked">
          <Checkbox>{t("tax")}</Checkbox>
        </Form.Item>
        <Form.Item shouldUpdate={() => true} label={"تخفیف"}>
          {({ getFieldValue }) => (
            <Space.Compact className={"tw-w-full"}>
              <Form.Item
                label={t("invoice.detail.discount")}
                noStyle
                name="discountAmount"
                initialValue={0}
                rules={[
                  getFieldValue("discountType") === 0
                    ? { max: 99, min: 0, transform: (value): number => unmaskStrToNum(value), type: "number" }
                    : { transform: (value): number => unmaskStrToNum(value), type: "number" },
                ]}
              >
                <Input.Number
                  className="tw-w-full"
                  suffix={getFieldValue("discountType") === 0 ? "%" : <CuToman width="20px" height="20px" />}
                  separateThousandth={getFieldValue("discountType") !== 0}
                  inputMode="numeric"
                  changeOnWheel={false}
                  controls={false}
                />
              </Form.Item>
              <Form.Item noStyle name="discountType" initialValue={0}>
                <Select>
                  <Select.Option value={0}>{t("percent")}</Select.Option>
                  <Select.Option value={1}>{t("price")}</Select.Option>
                </Select>
              </Form.Item>
            </Space.Compact>
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ConfigItemModal;
